import { useEffect, useRef, useState } from 'react';
import { useBackdrop } from '@/stores/backdrop.ts';

/** Long enough that holding a direction does not fetch every poster it passes. */
const SETTLE_MS = 220;

/**
 * The room the app is in.
 *
 * Two layers, and the top one fades in over whatever is beneath it. A single
 * layer swapping its `background-image` flashes to empty for a frame while
 * the new file decodes — on a Fire TV Stick, several frames — which reads as
 * the screen blinking every time the cursor moves.
 *
 * DEBOUNCED, because holding a direction crosses a dozen posters a second and
 * each one would start a fetch and a crossfade. Waiting for the cursor to
 * settle means the background follows where somebody stopped, which is what
 * they were looking at anyway.
 *
 * WHEN THERE IS NO PICTURE, THERE IS THE BADGE. An empty room is just the
 * page colour, which is fine but says nothing; the house mark at a whisper
 * says whose app this is. It is deliberately NOT shown behind the guide —
 * see the CSS — because a watermark under a grid of programme names is the
 * same legibility problem the film backdrops caused there, only permanent.
 */
export function Backdrop() {
  const { url, suppressed } = useBackdrop();
  const set = useBackdrop((s) => s.set);

  // What is actually painted, which lags `url` by the settle time.
  const [shown, setShown] = useState<string | null>(null);
  const [under, setUnder] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
   * One listener for the whole document. An element declares its backdrop and
   * this notices — no component has to know this exists, which is the point.
   */
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-backdrop]');
      const next = el?.dataset.backdrop || null;
      // A focusable with nothing to say leaves the room as it was, rather
      // than blanking it — walking onto the navigation bar should not wipe
      // the picture you were just looking at.
      if (next) set(next);
    };
    document.addEventListener('focusin', onFocus, true);
    return () => document.removeEventListener('focusin', onFocus, true);
  }, [set]);

  useEffect(() => {
    if (url === shown) return;

    /*
     * CLEARING IS NOT A CROSSFADE, and treating it as one is what kept the
     * last film on screen all the way into Live TV.
     *
     * The two-layer dance exists to hide the decode of an INCOMING image:
     * the outgoing one is held underneath until the new one has covered it,
     * then dropped by a timer inside `onload`. With nothing incoming there is
     * no `onload` and so no timer — the old picture was moved to the bottom
     * layer and left there forever, which looked exactly like the backdrop
     * never being cleared at all. Nothing to fade to means drop both, now.
     *
     * It is also deliberately NOT debounced. The settle delay is there so
     * that sweeping the cursor across a row does not fetch every poster it
     * passes; a clear fetches nothing, and waiting 220ms to obey it is 220ms
     * of a film sitting behind the guide.
     */
    if (!url) {
      if (timer.current) clearTimeout(timer.current);
      setUnder(null);
      setShown(null);
      return;
    }

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      /*
       * PRELOAD, THEN SWAP. Setting the URL and hoping means the fade begins
       * against a transparent layer and the image appears partway through it,
       * which looks like a stutter rather than a dissolve.
       */
      const img = new Image();
      img.onload = () => {
        setUnder(shown);
        setShown(url);
        /*
         * Drop the outgoing layer once the incoming one has finished
         * covering it. Leaving it there is harmless while the top layer is
         * opaque, but it keeps a full-screen image decoded for no reason —
         * which on a Fire TV Stick is memory that could be a poster.
         */
        window.setTimeout(() => setUnder(null), 500);
      };
      // A broken backdrop is not worth reporting; the room just stays as it
      // was, which is the correct behaviour and needs no explanation.
      img.onerror = () => { /* keep what is there */ };
      img.src = url;
    }, SETTLE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [url, shown]);

  return (
    <div className={`backdrop${suppressed ? ' backdrop-off' : ''}`} aria-hidden="true">
      {/*
        Under both layers, so a film crossfading in covers it rather than
        blending with it. It is a background-image in CSS and not an <img> on
        purpose: if the file is not there, nothing is drawn and nothing
        complains, which is what should happen to a decoration.
      */}
      {!shown && !under && <div className="backdrop-badge" />}
      {/*
        THE FADE-TO-BOTTOM LIVES ON THIS WRAPPER, NOT ON EACH LAYER.
        
        Same mistake as the dimming, wearing a different hat. A mask that
        thins the picture towards the bottom makes every layer it is applied
        to partly transparent — so the incoming film did not COVER the
        outgoing one, it blended with it, and two films appeared at once
        across the lower two-thirds of the screen. A layer meant to replace
        another has to be opaque; anything that makes the result quiet belongs
        on a container above them both.
        
        The badge sits outside this on purpose: it is already faint, and
        putting it under the same mask would take it to about seven percent.
      */}
      <div className="backdrop-frames">
        {under && <div className="backdrop-layer" style={{ backgroundImage: `url(${under})` }} />}
        {shown && (
          <div
            key={shown}
            className="backdrop-layer backdrop-in"
            style={{ backgroundImage: `url(${shown})` }}
          />
        )}
      </div>
    </div>
  );
}
